(() => {
  'use strict';
  if (customElements.get('bundle-cart-validation')) return;

  const property = '_bread_box_components';
  const controls = () => [...document.querySelectorAll('bundle-cart-validation')];
  let revision = 0;
  let running = null;
  let timer;
  let permit = null;
  let hasBundles = false;
  let issues = [];

  function productURL(item) {
    const url = new URL(item.url, location.origin);
    if (url.origin !== location.origin || !/\/products\/[^/]+/.test(url.pathname)) {
      throw new Error('Invalid bundle product URL');
    }
    url.search = '';
    url.searchParams.set('variant', String(item.variant_id));
    return url;
  }

  async function request(url, json = true) {
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 10000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: abort.signal });
      if (!response.ok) throw new Error('Unable to verify bundle');
      return await (json ? response.json() : response.text());
    } finally {
      clearTimeout(timeout);
    }
  }

  function parseConfiguration(html, variantId) {
    const page = new DOMParser().parseFromString(html, 'text/html');
    const picker = [...page.querySelectorAll('[data-bread-box-bundle]')]
      .find(node => node.dataset.parentVariantId === String(variantId));
    const count = Number(picker?.dataset.requiredCount);
    if (!picker || !Number.isInteger(count) || count < 1) throw new Error('Bundle settings unavailable');
    const cards = [...picker.querySelectorAll('[data-bread-box-card]')];
    if (!cards.length) throw new Error('Bundle choices unavailable');
    return { count, eligible: new Set(cards.filter(card => card.dataset.available === 'true')
      .map(card => `gid://shopify/ProductVariant/${card.dataset.variantId}`)) };
  }

  function checkSelections(raw, config) {
    let selections;
    try { selections = JSON.parse(raw); } catch { return 'Your saved selections could not be read. Please choose your products again.'; }
    if (!Array.isArray(selections) || selections.some(id => typeof id !== 'string' || !/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(id))) {
      return 'Your saved selections could not be read. Please choose your products again.';
    }
    if (selections.length !== config.count) return `This bundle requires ${config.count} products. Please review your selections.`;
    const missing = selections.filter(id => !config.eligible.has(id)).length;
    return missing ? `${missing} ${missing === 1 ? 'selection is' : 'selections are'} no longer offered or available. Choose replacements before checking out.` : null;
  }

  function lock(pending, blocked) {
    document.querySelectorAll('[data-bundle-checkout]').forEach(button => {
      button.disabled = pending || blocked;
      button.setAttribute('aria-busy', String(pending));
    });
    // Wallet buttons live inside third-party frames; bundle carts use the guarded standard checkout.
    document.querySelectorAll('[data-bundle-express-checkout]').forEach(node => {
      node.hidden = pending || blocked || hasBundles;
    });
  }

  function issueContent(issue) {
    const p = document.createElement('p');
    const title = document.createElement('strong');
    title.textContent = `${issue.title}: `;
    p.append(title, document.createTextNode(issue.message + ' '));
    if (issue.url) {
      const link = document.createElement('a');
      link.href = issue.url;
      link.textContent = 'Edit bundle';
      p.append(link);
    }
    return p;
  }

  function render(pending = false) {
    lock(pending, issues.length > 0);
    controls().forEach(control => {
      const status = control.querySelector('[data-bundle-cart-status]');
      const retry = control.querySelector('[data-bundle-cart-retry]');
      status.replaceChildren();
      status.hidden = !pending && !issues.length;
      if (pending && !issues.length) status.textContent = 'Checking your bundle selections...';
      else issues.forEach(issue => status.append(issueContent(issue)));
      retry.hidden = pending || !issues.some(issue => issue.retry);
    });
    document.querySelectorAll('.sidebar .bundle-cart-line-warning').forEach(node => node.remove());
    if (!pending) document.querySelectorAll('.cart-form-item[data-js-cart-item]').forEach(row => {
      const issue = issues.find(issue => issue.key === row.dataset.id);
      const warning = row.querySelector('.bundle-cart-line-warning');
      if (!issue) { warning?.remove(); return; }
      const content = issueContent(issue);
      if (warning?.textContent === content.textContent && warning.querySelector('a')?.href === content.querySelector('a')?.href) return;
      if (warning) warning.replaceChildren(content);
      else {
        const newWarning = document.createElement('div');
        newWarning.className = 'bundle-cart-line-warning';
        newWarning.append(content);
        row.append(newWarning);
      }
    });
  }

  async function audit() {
    const cartPath = controls()[0]?.dataset.cartUrl;
    if (!cartPath) throw new Error('Cart unavailable');
    const cart = await request(`${cartPath}.js`);
    if (!Array.isArray(cart.items)) throw new Error('Cart unavailable');
    const knownParents = new Set([...document.querySelectorAll('[data-bundle-cart-line]')].map(node => node.dataset.variantId));
    const bundles = cart.items.filter(item => Object.prototype.hasOwnProperty.call(item.properties || {}, property) || knownParents.has(String(item.variant_id)));
    hasBundles = bundles.length > 0;
    const configurations = new Map();
    return (await Promise.all(bundles.map(async item => {
      let url;
      try {
        url = productURL(item);
        const key = url.href;
        if (!configurations.has(key)) configurations.set(key, request(key, false).then(html => parseConfiguration(html, item.variant_id)));
        const config = await configurations.get(key);
        const message = checkSelections(item.properties?.[property], config);
        url.hash = 'bread-box-bundle-picker';
        return message ? { key: item.key, title: item.product_title || item.title, message, url: url.href } : null;
      } catch {
        if (url) url.hash = 'bread-box-bundle-picker';
        return { key: item.key, title: item.product_title || item.title, url: url?.href, retry: true,
          message: "We couldn't verify this bundle. Please try again, or open it to review your selections." };
      }
    }))).filter(Boolean);
  }

  async function validate() {
    clearTimeout(timer);
    if (running) return running;
    running = (async () => {
      // A cart edit during a request invalidates its result; never unlock from stale data.
      let checked;
      do {
        checked = revision;
        render(true);
        try { issues = await audit(); }
        catch { issues = [{ title: 'Cart check', retry: true, message: "We couldn't verify your cart. Please try again before checking out." }]; }
      } while (checked !== revision);
      render();
      return !issues.length;
    })();
    try { return await running; } finally { running = null; }
  }

  function schedule() {
    revision++;
    lock(true, true);
    clearTimeout(timer);
    timer = setTimeout(validate, 350);
  }

  function focusWarning() {
    const status = controls().map(control => control.querySelector('[data-bundle-cart-status]'))
      .find(node => node && node.getClientRects().length);
    status?.focus();
  }

  async function checkout(event, form, submitter, link) {
    if (permit === form && form) { permit = null; return; }
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!(await validate())) { focusWarning(); return; }
    if (link) { location.assign(link.href); return; }
    if (!form?.isConnected || (submitter && !submitter.isConnected)) return;
    permit = form;
    form.requestSubmit(submitter || undefined);
    permit = null;
  }

  document.addEventListener('click', event => {
    const target = event.target.closest?.('[data-bundle-checkout], a[href]');
    if (!target || !controls().length) return;
    if (target.matches('[data-bundle-checkout]')) checkout(event, target.form, target);
    else {
      const url = new URL(target.href, location.origin);
      if (url.origin === location.origin && /\/checkout\/?$/.test(url.pathname)) checkout(event, null, null, target);
    }
  }, true);
  document.addEventListener('submit', event => {
    if (event.target.id === 'cart' && controls().length && (event.submitter?.name === 'checkout' || !event.submitter)) {
      checkout(event, event.target, event.submitter || event.target.querySelector('[name="checkout"]') || document.querySelector('[data-bundle-checkout]'));
    }
  }, true);
  document.addEventListener('cart-updated', schedule, true);
  document.addEventListener('input', event => { if (event.target.closest?.('cart-form')) schedule(); }, true);
  document.addEventListener('click', event => {
    if (event.target.closest?.('[data-js-cart-item] .remove, cart-product-quantity, [data-js-site-cart-sidebar-toggle], [aria-controls="site-cart-sidebar"]')) schedule();
  }, true);
  window.addEventListener('pageshow', schedule);
  window.addEventListener('focus', () => { if (controls().length) schedule(); });

  class BundleCartValidation extends HTMLElement {
    connectedCallback() {
      hasBundles ||= this.dataset.hasBundles === 'true';
      this.querySelector('[data-bundle-cart-retry]')?.addEventListener('click', () => { revision++; validate(); });
      schedule();
    }
  }
  customElements.define('bundle-cart-validation', BundleCartValidation);

  // The Local theme replaces cart HTML after adds/removals and opens its drawer via attributes.
  const drawer = document.querySelector('[data-js-site-cart-sidebar]');
  if (drawer) new MutationObserver(schedule).observe(drawer, { attributes: true, attributeFilter: ['class', 'style', 'aria-hidden', 'open'] });
})();
